// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { StyledButton } from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { qtyFieldStyles } from "@turnerBlueprint/App/Components/Qty/QtyField";
import { FormFieldLabel } from "@turnerBlueprint/MobiusOverrides/FormField";
import { fontConfig } from "@turnerBlueprint/XDL/PreStyle/typography";
import { color, xScreen } from "@turnerBlueprint/XDL/Utilities";
import { css } from "styled-components";

qtyFieldStyles.actionButton = {
    css: css`
        background-color: ${color("common.ui2")};
        display: flex;
        flex: 0 0 22px;
        align-items: center;
        height: 100%;
        justify-content: center;
        ${IconWrapper} {
            color: ${color("common.ui5")};
        }
        &:first-of-type {
            border-right: 1px solid ${color("common.ui5")};
        }

        &:last-of-type {
            border-left: 1px solid ${color("common.ui5")};
        }
    `,
};

qtyFieldStyles.wrapper = {
    css: css`
        background: ${color("common.ui1")};
        display: flex;
        align-items: center;
        height: 32px;
        min-width: 82px;
        width: 82px;
        overflow: hidden;
        overflow-x: hidden;
        ${({ showActionButton }: any) =>
            !showActionButton &&
            `
                min-width: 40px;
                width: 40px;
        `}
        &:not(${FormFieldLabel}) {
            border: 1px solid ${color("common.ui5")};

            &:hover {
                border-color: ${color("common.ui9")};
                > ${StyledButton} {
                    background: ${color("common.ui9")};

                    &:first-of-type,
                    &:last-of-type {
                        border-color: ${color("common.ui9")};
                    }

                    ${IconWrapper} {
                        color: ${color("common.ui1")};
                    }
                }
            }

            &:focus-within {
                border-color: ${color("common.ui10")};
                > ${StyledButton} {
                    border-color: ${color("common.ui10")};
                }
            }
        }
    `,
};

qtyFieldStyles.editableQuantityTextField = {
    sizeVariant: "small",
    mergeCss: true,
    cssOverrides: {
        formField: css`
            ${TypographyStyle} {
                text-align: center;
            }

            &&& {
                input {
                    &,
                    &:hover:not(:disabled),
                    &:focus:not(:disabled) {
                        border: none;
                        border-radius: 0;
                        outline: none;
                        box-shadow: none;
                        padding: 0 2px;
                    }
                }

                ${({ showActionButton }: any) =>
                    !showActionButton &&
                    `
                    input{
                        height: 30px;
                    }
                `}
            }
        `,
        formInputWrapper: css`
            &,
            &:hover:not(:disabled),
            &:focus:not(:disabled) {
                border: none;
                box-shadow: none;
                padding: 0;
            }
        `,
        inputSelect: css`
            font-size: 15px;
            line-height: 17px;
            text-align: center;
            height: 27px;
        `,
    },
};

qtyFieldStyles.container = {
    css: css`
        display: inline-flex;
        gap: 5px;
        align-items: center;
        flex-wrap: wrap;
        max-width: 114px;
    `,
};

qtyFieldStyles.questionIconStyles = {
    size: 14,
    css: css`
        &,
        & > svg {
            opacity: 0.44;
            color: ${color("secondary")};
            &:hover {
                opacity: 1;
            }
        }
    `,
};

qtyFieldStyles.valueText = {
    size: 16,
    lineHeight: "17px",
    weight: fontConfig.weight.bold,
};

qtyFieldStyles.qtyLabel = {
    size: 12,
    lineHeight: "13px",
    weight: fontConfig.weight.regular,
};

qtyFieldStyles.qtyLabelNotEditable = {
    size: 12,
    lineHeight: "13px",
    weight: "normal",
    css: css`
        line-height: 13px;
        text-transform: capitalize;
        padding: 0;
        margin-bottom: 8px;
        display: none;
        ${xScreen("lte", "md")} {
            display: block;
        }
        @media print {
            &&& {
                font-size: 14px;
                line-height: 16px;
            }
        }
    `,
};

qtyFieldStyles.qtyErrorMessage = {
    size: 14,
    lineHeight: "22px",
    css: css`
        flex: 0 0 110px;
    `,
};

qtyFieldStyles.noPriceTooltip = {
    cssOverrides: {
        tooltipClickable: css`
            margin-left: 4px;
            margin-bottom: 5px;
        `,
    },
};

qtyFieldStyles.noPriceIcon = {
    size: 20,
    color: "warning.main",
    css: css`
        &,
        & svg {
            height: auto;
        }
    `,
};

qtyFieldStyles.qtyLabelNotEditable = {
    size: 12,
    lineHeight: "14px",
    css: css`
        display: none;
        ${xScreen("lte", "md")} {
            display: block;
            margin-bottom: 4px;
        }
    `,
};
